import { useMemo } from 'react';
import moment, { Moment } from 'moment';
import { cloneDeep } from 'lodash';
import { MOBILE_STORES } from './constants';
import { MobileStore } from './types';

/*
  Склонение существительных в зависимости от числа
  Пример использования: integerDeclination(5, ['год', 'года', 'лет'])
  Результат: "лет"
*/
export const integerDeclination = (
  number: number,
  titles: string[]
): string => {
  const cases = [2, 0, 1, 1, 1, 2];

  return titles[
    number % 100 > 4 && number % 100 < 20
      ? 2
      : cases[number % 10 < 5 ? number % 10 : 5]
  ];
};

/** Удаление HTML-тегов из строки */
export const removeHtmlTags = (html: string): string => {
  if (!html) {
    return '';
  }

  return html
    .replace(/<[^>]*>/g, '') // Удаляем все HTML-теги
    .replace(/&nbsp;/g, ' ') // Заменяем неразрывные пробелы
    .replace(/&amp;/g, '&') // Заменяем HTML-сущности
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

/* Форматирование номера телефона из формата +79810782222 в +7 (981) 078 22-22 */
export const formatStrNumberWithCode = (phone: string): string => {
  const formatedPhone = phone.split('');
  formatedPhone.splice(2, 0, ' (');
  formatedPhone.splice(6, 0, ') ');
  formatedPhone.splice(10, 0, ' ');
  formatedPhone.splice(13, 0, '-');

  return formatedPhone.join('');
};

/* Определение магазина мобильного приложения */
export const detectStore = (): MobileStore => {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('huawei') || ua.includes('honor')) {
    return 'huawei';
  }

  return (
    MOBILE_STORES.find((store) =>
      store.keywords.some((keyword: string) => ua.includes(keyword))
    )?.value ?? 'ru'
  );
};

/* Получение ссылки на мобильное приложение для магазина */
export const useMobileAppLink = (store: MobileStore) =>
  useMemo(
    () => MOBILE_STORES.find((s) => s.value === store)?.link ?? '',
    [store]
  );

/**
 * Возвращает строку с разницей между двумя датами
 *
 * @example
 * getPassedDaysStr(now, past)              // "2 года и 3 месяца и 5 дней назад"
 * getPassedDaysStr(now, future)            // "Через 1 год и 2 месяца"
 * getPassedDaysStr(now, past, { compact: true })  // "2 г. 3 мес. 5 д. назад"
 */
export const getPassedDaysStr = (
  initialDate: Moment,
  overdueDate: Moment,
  options?: { compact?: boolean }
): string => {
  const now = cloneDeep(initialDate);
  const overdue = cloneDeep(overdueDate);

  if (now.isSame(overdue, 'day')) {
    return 'Сегодня';
  }

  const diffYears = now.diff(overdue, 'year');
  overdue.add(diffYears, 'years');
  const diffMonths = now.diff(overdue, 'months');
  overdue.add(diffMonths, 'months');
  const diffDays =
    now.diff(overdue, 'days') !== 0 ? now.diff(overdue, 'days') : 1;

  const compact = options?.compact;

  const units = [
    {
      diff: diffYears,
      full: ['год', 'года', 'лет'],
      short: ['г.', 'г.', 'л.'],
    },
    {
      diff: diffMonths,
      full: ['месяц', 'месяца', 'месяцев'],
      short: ['мес.', 'мес.', 'мес.'],
    },
    {
      diff: diffDays,
      full: ['день', 'дня', 'дней'],
      short: ['д.', 'д.', 'д.'],
    },
  ];

  const result = units
    .filter(({ diff }) => Math.abs(diff) !== 0)
    .map(
      ({ diff, full, short }) =>
        `${Math.abs(diff)} ${integerDeclination(
          Math.abs(diff),
          compact ? [...short] : [...full]
        )}`
    )
    .join(compact ? ' ' : ' и ');

  if (overdue.isAfter(now)) {
    return `Через ${result}`.trim();
  }

  return `${result} назад`.trim();
};

export const getMeterTariff = (length: number, index: number) => {
  switch (length) {
    case 2:
      return index === 0 ? 'День' : 'Ночь';
    case 3:
      return index === 0 ? 'Пик' : index === 1 ? 'Ночь' : 'Полупик';
    default:
      return '';
  }
};
